import { Response } from 'request-dot-js'

import { WorkspacePortalClient, WorkspacePortalData, PortalServiceType } from 'api/clients/WorkspacePortalClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import * as Actions from 'redux/actions'
import store from 'redux/store'
import { PortalSectionType, SectionLayout, OptionalPortalSectionItemContent } from 'types'

const { getPortal: get, createPortal: create, updatePortal: update, destroyPortal: destroy } = WorkspacePortalClient

const PORTAL = 'portal'
const PAGE = 'page'
const SECTION = 'section'
const SECTION_ITEM = 'section_item'

type Data = { [key: string]: string | number | {} | undefined }

const send = async (
  clientCall: (key: string, type: PortalServiceType, data?: Data) => Promise<Response<WorkspacePortalData, any>>,
  type: PortalServiceType,
  data?: Data,
): Promise<Response<WorkspacePortalData> | undefined> => {
  const { auth } = store.getState()
  if (auth && auth.company_key) {
    const res = await clientCall(auth.company_key, type, data)
    if (res.type === SUCCESS_RESPONSE_TYPE && res.data) {
      store.dispatch(Actions.portalDataSet(res.data.workspace_portal))
    }
    return res
  }
}

// Portal
const getPortal = async () => {
  const res = await send(get, PORTAL)
  if (res && res.type === SUCCESS_RESPONSE_TYPE) {
    store.dispatch(Actions.portalLoaded())
  }
  return res
}

const createPortal = (data?: {
  title?: string
  description?: string
  logo_url?: string
  primary_color?: string
  secondary_color?: string
  text_color?: string
}) => send(create, PORTAL, data)

const updatePortal = (data: {
  title?: string
  description?: string
  logo_url?: string
  primary_color?: string
  secondary_color?: string
  text_color?: string
}) => send(update, PORTAL, data)

const destroyPortal = () => send(destroy, PORTAL)

// Page
const createPage = (data?: { position?: number; title?: string }) => send(create, PAGE, data)
const updatePage = (data: { id: number; position?: number; title?: string }) => send(update, PAGE, data)
const destroyPage = (id: number) => send(destroy, PAGE, { id })

// Section
const createSection = (data: {
  workspace_page_id: number
  section_type: PortalSectionType
  title?: string
  description?: string
  position?: number
  config?: {
    layout?: SectionLayout
  }
}) => send(create, SECTION, data)

const updateSection = (data: {
  id: number
  section_type?: PortalSectionType
  title?: string
  description?: string
  position?: number
  config?: {
    layout?: SectionLayout
  }
}) => send(update, SECTION, data)

const destroySection = (id: number) => send(destroy, SECTION, { id })

// Section Item
const createSectionItem = (data: {
  workspace_section_id: number
  content?: OptionalPortalSectionItemContent
  position?: number
}) => send(create, SECTION_ITEM, data)

const updateSectionItem = (data: { id: number; content?: OptionalPortalSectionItemContent; position?: number }) =>
  send(update, SECTION_ITEM, data)

const destroySectionItem = (id: number) => send(destroy, SECTION_ITEM, { id })

const portalEditModeSet = (isEditMode: boolean) => {
  store.dispatch(Actions.portalEditModeSet(isEditMode))
}

const portalReset = () => {
  store.dispatch(Actions.portalReset())
}

export const WorkspacePortalService = {
  createPage,
  createPortal,
  createSection,
  createSectionItem,
  destroyPage,
  destroyPortal,
  destroySection,
  destroySectionItem,
  getPortal,
  portalEditModeSet,
  portalReset,
  updatePage,
  updatePortal,
  updateSection,
  updateSectionItem,
}
